function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t["return"] && (u = t["return"](), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
import { LinkButton } from '@hypothesis/frontend-shared';
import classnames from 'classnames';
import { useCallback, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { observeElementSize } from '../utils/observe-element-size';
import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
/**
 * An optional toggle link at the bottom of an excerpt which controls whether
 * it is expanded or collapsed.
 */
function InlineControls(_ref) {
  var isCollapsed = _ref.isCollapsed,
    setCollapsed = _ref.setCollapsed,
    linkStyle = _ref.linkStyle;
  return _jsx("div", {
    className: classnames(
    // Position these controls at the bottom right of the excerpt
    'absolute block right-0 bottom-0',
    // Give extra width for larger tap target and gradient fade
    // Fade transparent-to-white left-to-right to make the toggle
    // control text (More/Less) more readable above other text.
    // This gradient is implemented to-left to take advantage of Tailwind's
    // automatic to-transparent calculation: this avoids Safari's problem
    // with transparents in gradients:
    // https://bugs.webkit.org/show_bug.cgi?id=150940
    // https://tailwindcss.com/docs/gradient-color-stops#fading-to-transparent
    'w-20 bg-gradient-to-l from-white'),
    children: _jsx("div", {
      className: "flex justify-end",
      children: _jsx(LinkButton, {
        variant: "text",
        onClick: function onClick() {
          return setCollapsed(!isCollapsed);
        },
        expanded: !isCollapsed,
        title: "Toggle visibility of full excerpt text",
        style: linkStyle,
        underline: "always",
        inline: true,
        children: isCollapsed ? 'More' : 'Less'
      })
    })
  });
}
var noop = function noop() {};
/**
 * A container which truncates its content when they exceed a specified height.
 *
 * The collapsed state of the container can be handled either via internal
 * controls (if `inlineControls` is `true`) or by the caller using the
 * `collapse` prop.
 */
export default function Excerpt(_ref2) {
  var children = _ref2.children,
    _ref2$collapse = _ref2.collapse,
    collapse = _ref2$collapse === void 0 ? false : _ref2$collapse,
    collapsedHeight = _ref2.collapsedHeight,
    _ref2$inlineControls = _ref2.inlineControls,
    inlineControls = _ref2$inlineControls === void 0 ? true : _ref2$inlineControls,
    _ref2$onCollapsibleCh = _ref2.onCollapsibleChanged,
    onCollapsibleChanged = _ref2$onCollapsibleCh === void 0 ? noop : _ref2$onCollapsibleCh,
    _ref2$onToggleCollaps = _ref2.onToggleCollapsed,
    onToggleCollapsed = _ref2$onToggleCollaps === void 0 ? noop : _ref2$onToggleCollaps,
    _ref2$overflowThresho = _ref2.overflowThreshold,
    overflowThreshold = _ref2$overflowThresho === void 0 ? 0 : _ref2$overflowThresho,
    _ref2$inlineControlsL = _ref2.inlineControlsLinkStyle,
    inlineControlsLinkStyle = _ref2$inlineControlsL === void 0 ? {} : _ref2$inlineControlsL;
  var _useState = useState(true),
    _useState2 = _slicedToArray(_useState, 2),
    collapsedByInlineControls = _useState2[0],
    setCollapsedByInlineControls = _useState2[1];
  var contentElement = useRef(null);

  // Measured height of `contentElement` in pixels
  var _useState3 = useState(0),
    _useState4 = _slicedToArray(_useState3, 2),
    contentHeight = _useState4[0],
    setContentHeight = _useState4[1];

  // Update the measured height of the content container after initial render,
  // and when the size of the content element changes.
  var updateContentHeight = useCallback(function () {
    var newContentHeight = contentElement.current.clientHeight;
    setContentHeight(newContentHeight);

    // prettier-ignore
    var isCollapsible = newContentHeight > collapsedHeight + overflowThreshold;
    onCollapsibleChanged(isCollapsible);
  }, [collapsedHeight, onCollapsibleChanged, overflowThreshold]);
  useLayoutEffect(function () {
    var cleanup = observeElementSize(contentElement.current, updateContentHeight);
    updateContentHeight();
    return cleanup;
  }, [updateContentHeight]);

  // Render the (possibly truncated) content and controls for
  // expanding/collapsing the content.
  // prettier-ignore
  var isOverflowing = contentHeight > collapsedHeight + overflowThreshold;
  var isCollapsed = inlineControls ? collapsedByInlineControls : collapse;
  var isExpandable = isOverflowing && isCollapsed;
  var contentStyle = {};
  if (contentHeight !== 0) {
    contentStyle['max-height'] = isExpandable ? collapsedHeight : contentHeight;
  }
  var setCollapsed = function setCollapsed(collapsed) {
    return inlineControls ? setCollapsedByInlineControls(collapsed) : onToggleCollapsed(collapsed);
  };
  return _jsxs("div", {
    "data-testid": "excerpt-container",
    className: classnames('relative overflow-hidden', 'transition-[max-height] ease-in duration-150'),
    style: contentStyle,
    children: [_jsx("div", {
      className: classnames(
      // Establish new block-formatting context to prevent margin-collapsing
      // in descendent elements from potentially "leaking out" and pushing
      // this element down from the top of the container.
      // See https://developer.mozilla.org/en-US/docs/Web/Guide/CSS/Block_formatting_context
      // See https://github.com/hypothesis/client/issues/1518
      'inline-block w-full'),
      "data-testid": "excerpt-content",
      ref: contentElement,
      children: children
    }), _jsx("div", {
      "data-testid": "excerpt-expand",
      role: "presentation",
      onClick: function onClick() {
        return setCollapsed(false);
      },
      className: classnames(
      // This element provides a clickable area at the bottom of an
      // expandable excerpt to expand it.
      'transition-[opacity] duration-150 ease-linear', 'absolute w-full bottom-0 h-touch-minimum', {
        // For expandable excerpts not using inlineControls, style this
        // element with a custom shadow-like gradient
        'bg-gradient-to-b from-excerpt-stop-1 via-excerpt-stop-2 to-excerpt-stop-3': !inlineControls && isExpandable,
        'bg-none': inlineControls,
        // Don't make this shadow visible OR clickable if there's nothing
        // to do here (the excerpt isn't expandable)
        'opacity-0 pointer-events-none': !isExpandable
      }),
      title: "Show the full excerpt"
    }), isOverflowing && inlineControls && _jsx(InlineControls, {
      isCollapsed: collapsedByInlineControls,
      setCollapsed: setCollapsed,
      linkStyle: inlineControlsLinkStyle
    })]
  });
}
//# sourceMappingURL=Excerpt.js.map